import React from 'react';
import { inject, observer } from 'mobx-react';
import type { AppStore } from '@stores/app/store';
import type { SiteStore } from '@stores/site/store';
import type { UserAction } from '@stores/user/action';
import PC from './layout/pc';
import H5 from './layout/h5';
import { BasePage } from '@components/Base/BasePage';
import styles from './index.module.scss';
import type { BasePageProps } from '../../types/base';
import { withRouter } from 'react-router-dom';
import { RouteComponentProps } from 'react-router-dom';

interface RouteParams {
    code: string;
}

interface UserInfo {
    code: string;
    nickname?: string;
    avatar?: string;
    follow?: number;
    isFollowed?: boolean;
    [key: string]: any;  // 其他可能的属性
}
interface NotificationsListProps extends BasePageProps, RouteComponentProps<RouteParams> {
    app?: AppStore;
    site?: SiteStore;
    user?: UserAction;
}

interface NotificationsListState {
    loading: boolean;
    error: any
    pageLoaded: any
    userInfo: UserInfo
    filterValue: any
    navList: any
    navCurrentIndex: any
    navCurrentType: number
    unreadNotificationsData: any
}
@inject('app', 'site', 'user')
@observer
class NotificationsList extends BasePage<NotificationsListProps, NotificationsListState> {
    namespace='userNotificationsList';
    isLoadingMore: boolean = false;         // 防止重复加载更多
    state = {
        loading: false,
        error: null,
        pageLoaded: false,
        userInfo: { code: '' },  // 提供默认值
        filterValue: '',    // 消息类型参数
        navList: [
            {name: '评论', id: 0, type: 0, filterValue: 'replied', num: 0 },
            {name: '@我', id: 1, type: 1, filterValue: 'related', num: 0},
        ],  // tab切换列表
        navCurrentIndex: 0, // 导航当前选中索引
        navCurrentType: 0,  // 导航当前选中类型
        unreadNotificationsData: {},    // 未读消息数据
    };
    componentDidMount() {
        const { user, match } = this.props;
        // 从路由读取 tab 参数
        const routerParams = new URLSearchParams(this.props.location.search);
        const tabParam = Number(routerParams?.get('tab')) || 0;

        // 先重置列表状态
        this.resetList();

        // 获取路由参数 code
        const code = match.params.code;
        if (!user || !code) {
            super.componentDidMount();
            return;
        }

        // 根据 tab 参数计算 filterValue
        const newFilterValue = tabParam === 0
            ? 'replied,related'
            : tabParam === 1
                ? 'system,thread,post,trial_product,auction'
                : 'liked,favorited';

        user.getUserInfo({ code }).then(res => {
            if (res.code === 0) {
                this.setState({
                    userInfo: res.data,
                    filterValue: newFilterValue,
                }, () => {
                    this.loadTabDataByNamespace(code);
                    this.getUnreadNotifications();
                });
            }
        });

        super.componentDidMount();
    }
    componentDidUpdate(prevProps: NotificationsListProps, prevState: NotificationsListState) {
        const routerParams = new URLSearchParams(this.props.location.search);
        const tabParam = Number(routerParams?.get('tab')) || 0;
        const prevRouterParams = new URLSearchParams(prevProps.location.search);
        const prevTabParam = Number(prevRouterParams?.get('tab')) || 0;

        // 路由 code 变化或 tab 参数变化时重新加载
        const codeChanged = prevProps.match.params.code !== this.props.match.params.code;
        const tabChanged = prevTabParam !== tabParam;

        if (codeChanged || tabChanged) {
            const { user, match } = this.props;
            const code = match.params.code;

            // 重置列表状态
            this.resetList();
            // 重置分页加载状态
            this.isLoadingMore = false;

            if (!user || !code) return;

            // 根据 tab 参数计算 filterValue
            const newFilterValue = tabParam === 0
                ? 'replied,related'
                : tabParam === 1
                    ? 'system,thread,post,trial_product,auction'
                    : 'liked,favorited';

            // tab 切换时直接加载，code 切换时重新获取用户信息
            if (tabChanged && !codeChanged) {
                this.setState({
                    filterValue: newFilterValue,
                    navCurrentIndex: 0,
                    navCurrentType: 0,
                }, () => {
                    this.loadTabDataByNamespace(code);
                });
                return;
            }

            // code 切换时获取用户信息后再加载
            user.getUserInfo({ code }).then(res => {
                if (res.code === 0) {
                    this.setState({
                        userInfo: res.data,
                        filterValue: newFilterValue,
                        navCurrentIndex: 0,
                        navCurrentType: 0,
                    }, () => {
                        this.loadTabDataByNamespace(code);
                        this.getUnreadNotifications();
                    });
                }
            });
        }
    }
    
    // 获取未读消息数
    getUnreadNotifications = async () => {
        const { app, user } = this.props;
        const { navList } = this.state;
        if (!app || app.platform !== 'pc') return;
        if(user) {
            const res = await user.getUnreadNotifications();
            console.log(res, '未读消息数');
            this.setState({
                unreadNotificationsData: res.data
            }, () => {
                let navListCopy = navList;
                
                // const relatedAndrepliedUnreadNum = (res.data?.typeUnreadNotifications?.replied + res.data?.typeUnreadNotifications?.related) || 0;  // 评论和@未读消息数
                const systemUnreadNum = (res.data?.typeUnreadNotifications?.system + res.data?.typeUnreadNotifications?.thread + res.data?.typeUnreadNotifications?.post + res.data?.typeUnreadNotifications?.trial_product + res.data?.typeUnreadNotifications?.auction) || 0;  // 系统通知未读消息数
                const likeAndFavoriteUnreadNum = (res.data?.typeUnreadNotifications?.liked + res.data?.typeUnreadNotifications?.favorited) || 0;  // 点赞和收藏未读消息数
                navListCopy[0].num = res.data?.typeUnreadNotifications?.replied || 0;
                navListCopy[1].num = res.data?.typeUnreadNotifications?.related || 0;
                this.setState({
                    navList: navListCopy
                })

            })
        }
    }

    // filter[type]：system系统通知, related艾特@我的, replied回复我的, liked,favorited 点赞和收藏
    // 调用前需确保已通过 resetList 清空列表
    loadTabDataByNamespace = (code: string) => {
        const { user } = this.props;
        const { filterValue } = this.state;
        if (!user || !code) return;

        const params = {
            'page[number]': 1,
            'page[size]': 20,
            'filter[type]': filterValue,
        };

        user.fetchNotificationsList(params);
    }

    // 清空消息列表数据，避免复用上一次列表数据
    resetList = () => {
        const { user } = this.props;
        const store = user?.listStore;
        if (!store?.clearList) return;

        const namespace = this.namespace; // 'userNotificationsList'
        store.clearList({ namespace });
        store.setAttribute?.({
            namespace,
            key: 'requestError',
            value: { isError: false, errorText: '' }
        });
    }

    // 导航点击 - 仅处理 tab=0 内的子 tab 切换（评论 ↔ @我）
    navClick = (item: any, index: any) => {
        // 如果点击的是当前选中的子tab，不处理
        if (this.state.navCurrentIndex === index) return;

        const { match } = this.props;
        const code = match.params.code;
        if (!code) return;

        // 重置分页加载状态
        this.isLoadingMore = false;

        // 更新子 tab 状态，使用子 tab 对应的 filterValue 加载数据
        this.setState({
            navCurrentIndex: index,
            navCurrentType: item.type,
            filterValue: item.filterValue,
        }, () => {
            // 切换子 tab：先清空列表数据（不同子 tab 数据不同），再重新加载
            this.resetList();
            this.loadTabDataByNamespace(code);
        });
    }
    // 分页 - 加载更多
    handleLoadMore = () => {
        const { user, match } = this.props;
        const { userInfo, filterValue } = this.state;

        const code = match.params.code || userInfo.code;
        if (!user || !code || !filterValue) return;

        const namespace = this.namespace;
        const list = user.listStore.lists[namespace];
        if (!list) return;

        // 判断是否还有更多数据
        if (!list.hasMore) return;

        // 防止重复加载
        if (this.isLoadingMore) return;

        // 获取分页信息
        const currentPage = list.attribs?.currentPage || list.attribs?.page || 1;
        const pageSize = list.attribs?.pageSize || 20;
        const nextPage = currentPage + 1;

        // 保存请求时的上下文，回调时校验：用户是否切换 / tab 是否切换
        const requestCode = code;
        const requestFilterValue = filterValue;

        this.isLoadingMore = true;

        const params: any = {
            'page[number]': nextPage,
            'page[size]': pageSize,
            'filter[type]': filterValue,
        };

        user.fetchNotificationsList(params)
            .then((res: any) => {
                // 校验上下文：用户 code 或 tab 已切换则丢弃本次结果
                const currentCode = this.props.match.params.code || this.state.userInfo.code;
                const currentFilterValue = this.state.filterValue;
                if (requestCode !== currentCode || requestFilterValue !== currentFilterValue) {
                    return;
                }
                // 数据已由 fetchNotificationsList 写入 listStore，无需额外处理
            })
            .catch((error: any) => {
                console.error('加载更多消息通知失败', error);
            })
            .finally(() => {
                this.isLoadingMore = false;
            });
    }

    render() {
        const { app, user } = this.props;
        const { navList, navCurrentIndex, navCurrentType } = this.state;
        const platform = app?.platform || 'h5';
         // 获取列表数据和状态
        const namespace = 'userNotificationsList';
        const listStore = user?.listStore;
        const list = listStore?.lists[namespace];
        const loading = list?.loading || false;
        const hasMore = list?.hasMore ?? true;
        // 如果平台信息还未初始化，显示加载状态
        if (!app?.platform) {
            return (
                <div className={styles.loading} style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh'
                }}>
                    <div>加载中...</div>
                </div>
            );
        } else {
           return (<div className={platform === 'pc' ? styles.pcWrap : styles.h5Wrap}>
                {platform === 'pc' ? (<PC
                    {...this.props}
                    loading={loading}
                    hasMore={hasMore}
                    handleLoadMore={this.handleLoadMore}
                ></PC>) : (
                    <H5
                        {...this.props}
                        loading={loading}
                        hasMore={hasMore}
                        handleLoadMore={this.handleLoadMore}
                        navList={navList}
                        navCurrentIndex={navCurrentIndex}
                        navCurrentType={navCurrentType}
                        navClick={(item, index) => this.navClick(item, index)}
                    ></H5>
                )}
            </div>)
        }
        
    }
}

export default withRouter(NotificationsList);